import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useAuth } from '../hooks/useAuth'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import {BarChart3, Users, Heart, AlertTriangle, CheckCircle, XCircle, TrendingUp, Activity, Download, Ban, Trash2, Clock, Percent, MapPin, Calendar, Mail} from 'lucide-react'

export function AdminDashboard() {
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalRequests: 0,
    activeRequests: 0,
    totalDonations: 0,
    errors24h: 0,
    matchesCreated: 0,
    newUsersThisMonth: 0,
    matchRate: 0,
    avgResolutionTime: 0,
    livesSaved: 0
  })
  const [recentRequests, setRecentRequests] = useState<any[]>([])
  const [recentUsers, setRecentUsers] = useState<any[]>([])
  const [suspiciousRequests, setSuspiciousRequests] = useState<any[]>([])
  const [selectedTab, setSelectedTab] = useState<'overview' | 'moderation' | 'reports'>('overview')

  useEffect(() => {
    if (!user) {
      toast.error('Faça login para continuar')
      navigate('/')
      return
    }

    if (!isAdmin) {
      toast.error('Acesso negado: apenas administradores')
      navigate('/')
      return
    }

    loadDashboardData()
  }, [user, isAdmin])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      // Load profiles
      const { list: profiles } = await lumi.entities.profiles.list({ limit: 1000 })
      
      // Load requests
      const { list: requests } = await lumi.entities.requests.list({ limit: 1000 })
      
      // Calculate metrics
      const activeRequests = requests.filter((r: any) => r.status === 'active')
      const fulfilledRequests = requests.filter((r: any) => r.status === 'fulfilled')
      const totalDonations = profiles.reduce((sum: number, p: any) => sum + (p.totalDonations || 0), 0)
      
      // New users this month
      const now = new Date()
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const newUsersThisMonth = profiles.filter((p: any) => new Date(p.createdAt) >= firstDayOfMonth).length
      
      // Match rate (fulfilled / total)
      const matchRate = requests.length > 0 ? Math.round((fulfilledRequests.length / requests.length) * 100) : 0
      
      // Average resolution time (days)
      const avgResolutionTime = fulfilledRequests.length > 0
        ? Math.round(fulfilledRequests.reduce((sum: number, r: any) => {
            const created = new Date(r.createdAt).getTime()
            const updated = new Date(r.updatedAt).getTime()
            return sum + (updated - created) / (1000 * 60 * 60 * 24)
          }, 0) / fulfilledRequests.length)
        : 0
      
      // Lives saved (1 donation saves up to 4 lives)
      const livesSaved = totalDonations * 4
      
      setMetrics({
        totalUsers: profiles.length,
        totalRequests: requests.length,
        activeRequests: activeRequests.length,
        totalDonations,
        errors24h: 0,
        matchesCreated: fulfilledRequests.length,
        newUsersThisMonth,
        matchRate,
        avgResolutionTime,
        livesSaved
      })
      
      // Find suspicious requests (multiple requests from same IP, suspicious patterns)
      const suspicious = requests.filter((r: any) => 
        r.status === 'active' && (!r.contactEmail || !r.contactPhone)
      )
      setSuspiciousRequests(suspicious)

      // Recent requests (last 10)
      setRecentRequests(requests.slice(0, 10))

      // Recent users (last 10)
      setRecentUsers(profiles.slice(0, 10))
    } catch (error) {
      console.error('Failed to load dashboard:', error)
      toast.error('Erro ao carregar dados do painel')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      active: { color: 'bg-green-100 text-green-800', label: 'Ativo' },
      in_progress: { color: 'bg-blue-100 text-blue-800', label: 'Em Andamento' },
      fulfilled: { color: 'bg-purple-100 text-purple-800', label: 'Concluído' },
      closed: { color: 'bg-gray-100 text-gray-800', label: 'Fechado' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelado' }
    }
    const badge = badges[status] || badges.active
    return <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>{badge.label}</span>
  }

  const handleApproveRequest = async (requestId: string) => {
    try {
      await lumi.entities.requests.update(requestId, { status: 'active' })
      toast.success('Solicitação aprovada')
      loadDashboardData()
    } catch (error) {
      toast.error('Erro ao aprovar solicitação')
    }
  }

  const handleRejectRequest = async (requestId: string) => {
    if (!confirm('Tem certeza que deseja rejeitar esta solicitação?')) return
    try {
      await lumi.entities.requests.update(requestId, { status: 'cancelled' })
      toast.success('Solicitação rejeitada')
      loadDashboardData()
    } catch (error) {
      toast.error('Erro ao rejeitar solicitação')
    }
  }

  const handleDeleteRequest = async (requestId: string) => {
    if (!confirm('Tem certeza que deseja DELETAR permanentemente esta solicitação?')) return
    try {
      await lumi.entities.requests.delete(requestId)
      toast.success('Solicitação deletada')
      loadDashboardData()
    } catch (error) {
      toast.error('Erro ao deletar solicitação')
    }
  }

  const exportToCSV = () => {
    const csvData = [
      ['Data', 'Paciente', 'Tipo Sanguíneo', 'Cidade', 'Status', 'Email', 'Telefone'],
      ...recentRequests.map(r => [
        new Date(r.createdAt).toLocaleDateString('pt-BR'),
        r.patientName,
        r.bloodType,
        `${r.city}/${r.state}`,
        r.status,
        r.contactEmail || '',
        r.contactPhone || ''
      ])
    ]
    const csvContent = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `relatorio-doe-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    toast.success('Relatório exportado!')
  }

  const sendMassNotification = async () => {
    const message = prompt('Digite a mensagem para enviar a todos os doadores:')
    if (!message) return
    
    try {
      const { list: profiles } = await lumi.entities.profiles.list({ limit: 1000 })
      const emailPromises = profiles
        .filter((p: any) => p.isDonor)
        .map((p: any) => 
          lumi.tools.email.send({
            to: p.userId, // Assuming userId is email
            subject: '📢 Mensagem importante do Doe',
            html: `<p>Olá ${p.displayName},</p><p>${message}</p><p>Equipe Doe</p>`
          })
        )
      await Promise.all(emailPromises)
      toast.success(`Notificação enviada para ${emailPromises.length} doadores`)
    } catch (error) {
      toast.error('Erro ao enviar notificações')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Carregando painel administrativo...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 pb-20">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Painel Administrativo</h1>
          <p className="text-gray-600 mt-1">Visão geral e métricas da plataforma</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b">
          <button
            onClick={() => setSelectedTab('overview')}
            className={`px-4 py-2 font-medium transition-colors ${
              selectedTab === 'overview'
                ? 'text-red-600 border-b-2 border-red-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Visão Geral
          </button>
          <button
            onClick={() => setSelectedTab('moderation')}
            className={`px-4 py-2 font-medium transition-colors ${
              selectedTab === 'moderation'
                ? 'text-red-600 border-b-2 border-red-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Moderação ({suspiciousRequests.length})
          </button>
          <button
            onClick={() => setSelectedTab('reports')}
            className={`px-4 py-2 font-medium transition-colors ${
              selectedTab === 'reports'
                ? 'text-red-600 border-b-2 border-red-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Relatórios
          </button>
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <>
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-pink-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Vidas Salvas</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.livesSaved}</p>
                  <p className="text-xs text-gray-500 mt-1">1 doação = 4 vidas</p>
                </div>
                <Heart className="h-12 w-12 text-pink-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Usuários Cadastrados</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.totalUsers}</p>
                  <p className="text-xs text-gray-500 mt-1">+{metrics.newUsersThisMonth} este mês</p>
                </div>
                <Users className="h-12 w-12 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Solicitações Ativas</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.activeRequests}</p>
                  <p className="text-xs text-gray-500 mt-1">De {metrics.totalRequests} totais</p>
                </div>
                <Heart className="h-12 w-12 text-red-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Doações Confirmadas</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.totalDonations}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Taxa de Match</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.matchRate}%</p>
                  <p className="text-xs text-gray-500 mt-1">{metrics.matchesCreated} resolvidos</p>
                </div>
                <Percent className="h-12 w-12 text-purple-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Tempo Médio</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.avgResolutionTime}</p>
                  <p className="text-xs text-gray-500 mt-1">dias para resolver</p>
                </div>
                <Clock className="h-12 w-12 text-orange-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Erros (24h)</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.errors24h}</p>
                </div>
                <AlertTriangle className="h-12 w-12 text-yellow-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Status do Sistema</p>
                  <p className="text-lg font-bold text-green-600 flex items-center mt-2">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Operacional
                  </p>
                </div>
                <BarChart3 className="h-12 w-12 text-gray-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Requests */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Solicitações Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Paciente</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Tipo</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Local</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recentRequests.length > 0 ? (
                    recentRequests.map((req) => (
                      <tr key={req._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{req.patientName}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center justify-center w-12 h-8 rounded bg-red-100 text-red-700 font-bold text-sm">
                            {req.bloodType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{req.city}/{req.state}</td>
                        <td className="px-4 py-3">{getStatusBadge(req.status)}</td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(req.createdAt).toLocaleDateString('pt-BR')}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        Nenhuma solicitação encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Recent Users */}
        <Card>
          <CardHeader>
            <CardTitle>Usuários Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Nome</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Tipo Sanguíneo</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Local</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Doações</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Cadastro</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recentUsers.length > 0 ? (
                    recentUsers.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{user.displayName}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center justify-center w-12 h-8 rounded bg-red-100 text-red-700 font-bold text-sm">
                            {user.bloodType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{user.city}/{user.state}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium">
                            {user.totalDonations || 0} doações
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        Nenhum usuário encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

          </>
        )}

        {/* Moderation Tab */}
        {selectedTab === 'moderation' && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Solicitações Suspeitas ({suspiciousRequests.length})</span>
                  <Button onClick={sendMassNotification} className="bg-blue-600 hover:bg-blue-700">
                    <Mail className="h-4 w-4 mr-2" />
                    Notificação em Massa
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {suspiciousRequests.length > 0 ? (
                  <div className="space-y-4">
                    {suspiciousRequests.map((req) => (
                      <div key={req._id} className="border rounded-lg p-4 bg-yellow-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900">{req.patientName}</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Tipo: {req.bloodType} | {req.city}/{req.state} | {req.hospital}
                            </p>
                            <p className="text-sm text-gray-500 mt-2">{req.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              <span>📧 {req.contactEmail || '❌ Sem email'}</span>
                              <span>📱 {req.contactPhone || '❌ Sem telefone'}</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => handleApproveRequest(req._id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-sm"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleRejectRequest(req._id)}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 text-sm"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteRequest(req._id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-sm"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">✅ Nenhuma solicitação suspeita no momento</p>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Reports Tab */}
        {selectedTab === 'reports' && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Exportar Relatórios</span>
                  <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700">
                    <Download className="h-4 w-4 mr-2" />
                    Exportar CSV
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">📊 Relatório de Solicitações</h3>
                    <p className="text-sm text-gray-600">Todas as solicitações com dados de contato</p>
                    <Button onClick={exportToCSV} className="mt-4 w-full bg-blue-600 hover:bg-blue-700">
                      Baixar Solicitações.csv
                    </Button>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">👥 Relatório de Usuários</h3>
                    <p className="text-sm text-gray-600">Todos os doadores cadastrados</p>
                    <Button className="mt-4 w-full bg-blue-600 hover:bg-blue-700">
                      Baixar Usuários.csv
                    </Button>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">📈 Relatório de Impacto</h3>
                    <p className="text-sm text-gray-600">Métricas de doações e vidas salvas</p>
                    <Button className="mt-4 w-full bg-blue-600 hover:bg-blue-700">
                      Baixar Impacto.csv
                    </Button>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h3 className="font-bold text-gray-900 mb-2">🗺️ Relatório Geográfico</h3>
                    <p className="text-sm text-gray-600">Distribuição por cidade/estado</p>
                    <Button className="mt-4 w-full bg-blue-600 hover:bg-blue-700">
                      Baixar Geografia.csv
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* System Checklist */}
        {selectedTab === 'overview' && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Checklist de Produção</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">Política de Privacidade configurada (LGPD)</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">Termos de Uso configurados</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">Sistema de autenticação funcional</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">E-mails transacionais configurados</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">HTTPS ativo</span>
              </div>
              <div className="flex items-center space-x-3">
                <XCircle className="h-5 w-5 text-yellow-600" />
                <span className="text-sm text-gray-700">Sistema de logs de auditoria (em implementação)</span>
              </div>
              <div className="flex items-center space-x-3">
                <XCircle className="h-5 w-5 text-yellow-600" />
                <span className="text-sm text-gray-700">Verificação de e-mail (pendente)</span>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
      <Footer />
    </div>
  )
}
